import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { AthleteLinks } from "../../../parts/athlete-links";
import CompareSelected from "../../../parts/CompareSelected";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import DateInput from "../../../parts/inputs/date-input";
import SvgIcon from "../../../parts/SvgIcon";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { IndependentDateRangeComponent } from "./components/IndependentDateRangeComponent";
import NavigationComponent from "./components/NavigationComponent";
import { ReportsBackHandler } from "./components/ReportsBackHandler";
import FilterChips from "./FilterChips";
import { VideoListTable } from "./VideoListTable";

const VideoList = ({ locals, momentTZ, comesFromCheckInSummary }) => {
  const { t } = useTranslation("athlete-portal");
  const { query } = locals;

  const hasNextPage = locals.videoListMetrics
    ? locals.videoListMetrics.hasNextPage
    : false;
  const { pathname, athlete } = locals;
  // the page lives in the url, not in state: the filter modal resets it from
  // another component and a local counter would drift out of sync with it
  const currentPage = Number.parseInt(query.pageToShow, 10) || 1;
  const [selectedRange, setSelectedRange] = useState();
  const [compareState, setCompareState] = useState(false);
  const [videosToCompareState, setVideosToCompareState] = useState([]);
  const [startDateToShow, setStartDateToShow] = useState(
    query.fromDate
      ? moment(query.fromDate)
      : moment().subtract(1, "year").format("MMM DD, YYYY")
  );
  const [endDateToShow, setEndDateToShow] = useState(
    query.toDate ? moment(query.toDate) : moment()
  );
  const [videosToShow, setVideosToShow] = useState([]);

  // When selected range is changed, we will set the new start date based on the amount of weeks selected
  useEffect(() => {
    // this ensures that a range was selected
    if (selectedRange || selectedRange === 0) {
      // if the selected range is greater than 2, we are talking about months
      if (selectedRange > 2) {
        // we split it by 4 to get the amount of months
        const dateSend = moment.utc().subtract(selectedRange / 4, "months");
        handleStartDateInput(dateSend);
      } else {
        // if the selected range is 0, we are talking about lifetime.
        // this was just an arbitrary way to refer to the lifetime, the 0
        if (selectedRange === 0) {
          const dateSend = moment.utc().subtract(10, "years");
          handleStartDateInput(dateSend);
        } else {
          // else we are just talking about 2 weeks; subtract them
          const dateSend = moment.utc().subtract(selectedRange, "weeks");
          handleStartDateInput(dateSend);
        }
      }
    }
  }, [selectedRange]);

  /**
   * CompareVideos
   * @param {*} e
   */
  const handleCompareVideos = () => {
    if (!compareState) return setCompareState(true);
    const firstVideo = videosToShow.find(
      (element) => element.videoOriginal === videosToCompareState[0]
    );
    const secondVideo = videosToShow.find(
      (element) => element.videoOriginal === videosToCompareState[1]
    );

    setCompareState(false);

    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        "modal[type]": "watch-multiple-videos-modal",
        firstMedia: JSON.stringify(firstVideo),
        secondMedia: JSON.stringify(secondVideo),
      },
    });
  };

  /**
   * Change start date
   * @param {*} date
   */
  const handleStartDateInput = (date) => {
    if (startDateToShow !== moment(date).format("MMM DD, YYYY")) {
      const format = moment(date).format("YYYY-MM-DD");
      const dateSend = `${format}T20:00:00Z`;
      setStartDateToShow(moment(date).format("MMM DD, YYYY"));
      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          fromDate: dateSend,
          pageToShow: 1,
        },
      });
    }
  };
  /**
   * Change end date
   * @param {*} e
   */
  const handleEndDateInput = (date) => {
    if (endDateToShow !== moment(date).format("MMM DD, YYYY")) {
      const format = moment(date).format("YYYY-MM-DD");
      const dateSend = `${format}T20:00:00Z`;
      setEndDateToShow(moment(date).format("MMM DD, YYYY"));
      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          toDate: dateSend,
          pageToShow: 1,
        },
      });
    }
  };

  /**
   * Open filter modal
   */
  const openFilterModal = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        "modal[type]": "filter-exercise-videos-modal",
      },
    });
  };

  const loadMoreVideos = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      // the date range already lives in the url and refreshWith merges into it
      body: {
        pageToShow: currentPage + 1,
      },
    });
  };
  const removeFilter = (type) => {
    const newFiltersSet = JSON.parse(query.exerciseFilters).filter(
      (exercise) => exercise !== type
    );
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        exerciseFilters:
          newFiltersSet.length > 0 ? JSON.stringify(newFiltersSet) : "",
        pageToShow: 1,
      },
    });
  };
  const getSelectedVideo = (id) => {
    return videosToShow.find((element) => element.videoOriginal === id);
  };
  const clearAllFilters = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        exerciseFilters: "",
        pageToShow: 1,
        modal: "",
      },
    });
  };

  const handleSelectedRange = (selectedRange) => {
    setSelectedRange(selectedRange);
  };

  const getFormatedDate = (video) => {
    return moment.utc(video.date).format("MMM D, YYYY");
  };
  return (
    <main role="reports-wrapper" className="main-videos">
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(locals?.athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <NavigationComponent
        query={query}
        textKey="nav.calendarDayView"
        pathName={locals.pathname}
        classNames="mobile-hidden"
      />
      <article id="video-list" role="report-content">
        <header>
          <hgroup>
            <div
              className={
                comesFromCheckInSummary
                  ? "mobile-hidden flex gap-2 items-center"
                  : "mobile-hidden "
              }
            >
              {comesFromCheckInSummary ? (
                <SvgIcon
                  classNames="cursor-pointer "
                  onClickFn={() => {
                    history.back();
                  }}
                  src={"/img/new-icons/back-button.svg"}
                />
              ) : null}

              <h2 className="mobile-hidden">{t("videoList.title")}</h2>
            </div>
            <ReportsBackHandler title={t("videoList.title")} locals={locals} />

            <div role="buttons-container" className="mobile-hidden">
              <a
                role="button"
                data-button-type="secondary"
                data-icon
                onClick={openFilterModal}
              >
                <SvgIcon src="/img/new-icons/filter-white.svg" alt="back" />
                <span>{t("videoList.filterExercise")}</span>
              </a>
              {compareState && (
                <a
                  role="button"
                  data-button-type="secondary"
                  data-icon
                  onClick={() => setCompareState(false)}
                >
                  <img src="/img/new-icons/red-cross-20px.svg" alt="dismiss" />
                  <span>{t("calendar.cancel")}</span>
                </a>
              )}
              <a
                role="button"
                data-button-type="primary"
                // onClick={() => setCompareState(true)}
                onClick={handleCompareVideos}
                disabled={
                  compareState && videosToCompareState.length < 2
                    ? true
                    : undefined
                }
              >
                {t("videoList.compareVideos")}
              </a>
            </div>
          </hgroup>
          <hgroup className="only-show-mobile">
            <div role="buttons-container">
              <a
                role="button"
                data-button-type="secondary"
                data-icon
                onClick={openFilterModal}
              >
                <img src="/img/new-icons/filter-white.svg" alt="back" />
                <span>{t("videoList.filterExercise")}</span>
              </a>
              {compareState && (
                <a
                  role="button"
                  data-button-type="secondary"
                  data-icon
                  onClick={() => setCompareState(false)}
                >
                  <img src="/img/new-icons/red-cross-20px.svg" alt="dismiss" />
                  <span>{t("calendar.cancel")}</span>
                </a>
              )}
              <button
                data-button-type="primary"
                onClick={handleCompareVideos}
                disabled={
                  compareState && videosToCompareState.length < 2
                    ? true
                    : undefined
                }
                className={compareState && "mobile-hidden"}
              >
                {t("videoList.compareVideos")}
              </button>
            </div>
          </hgroup>
          <hgroup>
            <div role="calendar-date-selectors" className="mobile-hidden">
              <DateInput
                name="from"
                placeholder={t("reports.startDate")}
                value={startDateToShow}
                onChange={(e) => handleStartDateInput(e)}
                dontPadding={true}
              />
              <span>{t("reports.to")}</span>
              <DateInput
                name="to"
                placeholder={t("reports.endDate")}
                value={endDateToShow}
                dontPadding={true}
                onChange={(e) => handleEndDateInput(e)}
              />
            </div>
            {query.exerciseFilters ? (
              <FilterChips
                filters={
                  query.exerciseFilters
                    ? JSON.parse(query.exerciseFilters).map((filter) => {
                        return { text: filter, id: filter };
                      })
                    : []
                }
                clearAllFilters={clearAllFilters}
                clearFilter={removeFilter}
                overflowLimit={7}
                openFilterModal={openFilterModal}
              />
            ) : null}
            <IndependentDateRangeComponent
              weeksSelected={selectedRange}
              handleSelectedRange={handleSelectedRange}
              classNames="only-show-mobile"
            />
          </hgroup>
        </header>
        {compareState && (
          <p className="s3 mb-2">{t("videoList.selectTwoVideos")}</p>
        )}
        <section role="table-wrapper">
          <table role="report-table">
            <thead>
              {videosToShow && videosToShow.length > 0 ? (
                <tr>
                  {compareState && <th></th>}
                  <th>{t("reports.date")}</th>
                  <th>{t("videoList.exerciseName")}</th>
                  <th>{t("videoList.protocol")}</th>
                  <th>{t("videoList.comments")}</th>
                  <th className="small-th">{t("videoList.status")}</th>
                  <th className="small-th">{t("videoList.watched")}</th>
                  <th className="small-th">{t("videoList.video")}</th>
                </tr>
              ) : (
                <div className="empty-state">
                  <h3>{t("videoList.noVideosToShow")}</h3>
                </div>
              )}
            </thead>
            <VideoListTable
              locals={locals}
              query={query}
              compareState={compareState}
              setVideosToCompareState={setVideosToCompareState}
              videosToShow={videosToShow}
              setVideosToShow={setVideosToShow}
            />
          </table>
        </section>
        {hasNextPage && (
          <a
            role="button"
            data-button-type="secondary"
            onClick={loadMoreVideos}
          >
            {t("videoList.showMore")}
          </a>
        )}
      </article>
      <CompareSelected
        label1={"Video 1"}
        label2={"Video 2"}
        selectedItem1={
          videosToCompareState[0]
            ? getFormatedDate(getSelectedVideo(videosToCompareState[0]))
            : null
        }
        selectedItem2={
          videosToCompareState[1]
            ? getFormatedDate(getSelectedVideo(videosToCompareState[1]))
            : null
        }
        compareFunction={handleCompareVideos}
        closeFunction={() => setCompareState(false)}
        compareName={"Videos"}
      />
    </main>
  );
};

export default VideoList;
